import { useMemo } from 'react';
import { T, useGT } from 'gt-react';
import useOrganizationMembers from 'src/api/organization/useOrganizationMembers';
import { useListAgentDefinitions } from 'src/api/rpc/hooks/agent-definitions/useAgentDefinitions';
import { useUserGroupsList } from '@/api/rpc/hooks/organization/useUserGroups';
import { useOrganizationContacts } from '@/api/rpc/hooks/communications/useOrganizationContacts';
import { Label } from 'src/components/ui/label';
import {
  MultiSelect,
  type MultiSelectOption,
} from 'src/components/common/MultiSelect';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';

type TaskAssigneeType = 'user' | 'agent';
type TaskAssigneeMode = TaskAssigneeType | 'none';

interface TaskAssigneeConfigFieldsProps {
  config: Record<string, unknown>;
  onConfigChange: (newConfig: Record<string, unknown>) => void;
  label?: string;
  description?: string;
  /** When true, the assignee controls are disabled (read-only graph). */
  readOnly?: boolean;
}

function getTaskAssigneeMode(
  config: Record<string, unknown>,
): TaskAssigneeMode {
  return config.assigneeType === 'user' || config.assigneeType === 'agent'
    ? config.assigneeType
    : 'none';
}

function getStringValue(
  config: Record<string, unknown>,
  key: 'assigneeUserId' | 'assigneeAgentDefinitionId',
): string {
  const value = config[key];
  return typeof value === 'string' ? value : '';
}

export function TaskAssigneeConfigFields({
  config,
  onConfigChange,
  label: labelProp,
  description: descriptionProp,
  readOnly,
}: TaskAssigneeConfigFieldsProps) {
  const gt = useGT();
  const label =
    labelProp ??
    gt('Default Assignee', {
      context: 'workflow task default assignee label',
    });
  const description =
    descriptionProp ??
    gt('Used when assignee input sockets are not connected.', {
      context: 'workflow task assignee help when sockets disconnected',
    });
  const assigneeMode = getTaskAssigneeMode(config);
  const selectedUserId = getStringValue(config, 'assigneeUserId');
  const selectedAgentDefinitionId = getStringValue(
    config,
    'assigneeAgentDefinitionId',
  );

  const {
    data: membersData,
    isLoading: isMembersLoading,
    error: membersError,
  } = useOrganizationMembers(true);

  const {
    data: agentDefinitionsResponse,
    isLoading: isAgentDefinitionsLoading,
    error: agentDefinitionsError,
  } = useListAgentDefinitions({ limit: 200 });

  const activeMembers = (membersData?.members ?? []).filter(
    (member) => member.deactivatedAt === null,
  );
  const agentDefinitions = agentDefinitionsResponse?.definitions ?? [];

  // Group + contact selection — the same recipient model as the Notify step.
  // Members of selected groups (and contacts that resolve to users) are added
  // as watchers; when no explicit Assignee User is set, the first resolved
  // user becomes the assignee.
  const selectedGroupIds = useMemo(
    () =>
      Array.isArray(config.assigneeUserGroupIds)
        ? config.assigneeUserGroupIds.filter(
            (id): id is string => typeof id === 'string',
          )
        : [],
    [config.assigneeUserGroupIds],
  );
  const selectedContactIds = useMemo(
    () =>
      Array.isArray(config.assigneeContactIds)
        ? config.assigneeContactIds.filter(
            (id): id is string => typeof id === 'string',
          )
        : [],
    [config.assigneeContactIds],
  );
  const { data: groups } = useUserGroupsList();
  const groupOptions: MultiSelectOption[] = useMemo(
    () => (groups ?? []).map((g) => ({ id: g.id, label: g.name })),
    [groups],
  );
  const selectedGroupOptions = useMemo(
    () => groupOptions.filter((o) => selectedGroupIds.includes(o.id)),
    [groupOptions, selectedGroupIds],
  );
  const { contacts } = useOrganizationContacts();
  const contactOptions: MultiSelectOption[] = useMemo(
    () =>
      contacts
        .filter((c) => !!c.email)
        .map((c) => ({ id: c.id, label: `${c.name} <${c.email}>` })),
    [contacts],
  );
  const selectedContactOptions = useMemo(
    () => contactOptions.filter((o) => selectedContactIds.includes(o.id)),
    [contactOptions, selectedContactIds],
  );

  function handleAssigneeModeChange(nextMode: string) {
    const {
      assigneeType: _assigneeType,
      assigneeUserId: _assigneeUserId,
      assigneeUserGroupIds: _assigneeUserGroupIds,
      assigneeContactIds: _assigneeContactIds,
      assigneeAgentDefinitionId: _assigneeAgentDefinitionId,
      ...rest
    } = config;

    if (nextMode === 'user' || nextMode === 'agent') {
      onConfigChange({
        ...rest,
        assigneeType: nextMode,
      });
      return;
    }

    onConfigChange(rest);
  }

  function handleUserAssigneeChange(userId: string) {
    const { assigneeAgentDefinitionId: _assigneeAgentDefinitionId, ...rest } =
      config;

    onConfigChange({
      ...rest,
      assigneeType: 'user',
      assigneeUserId: userId,
    });
  }

  function handleAgentAssigneeChange(agentDefinitionId: string) {
    const { assigneeUserId: _assigneeUserId, ...rest } = config;

    onConfigChange({
      ...rest,
      assigneeType: 'agent',
      assigneeAgentDefinitionId: agentDefinitionId,
    });
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="assigneeMode" className="text-sm font-medium">
          {label}
        </Label>
        <Select
          value={assigneeMode}
          onValueChange={handleAssigneeModeChange}
          disabled={readOnly}
        >
          <SelectTrigger id="assigneeMode" className="w-full">
            <SelectValue
              placeholder={gt('Select assignee mode...', {
                context: 'placeholder task assignee mode',
              })}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">
              <T context="task assignee mode unassigned">Unassigned</T>
            </SelectItem>
            <SelectItem value="user">
              <T context="task assignee mode user">User</T>
            </SelectItem>
            <SelectItem value="agent">
              <T context="task assignee mode AI agent">AI Agent</T>
            </SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>

      {assigneeMode === 'user' && (
        <div className="space-y-2">
          <Label htmlFor="assigneeUserId" className="text-sm font-medium">
            <T context="label for user assignee dropdown">Assignee User</T>
          </Label>
          <Select
            value={selectedUserId}
            onValueChange={handleUserAssigneeChange}
            disabled={
              readOnly || isMembersLoading || activeMembers.length === 0
            }
          >
            <SelectTrigger id="assigneeUserId" className="w-full">
              <SelectValue
                placeholder={gt('Select a user...', {
                  context: 'placeholder select task assignee user',
                })}
              />
            </SelectTrigger>
            <SelectContent>
              {isMembersLoading && (
                <div className="px-2 py-1.5 text-xs text-muted-foreground">
                  <T context="loading organization members">Loading users...</T>
                </div>
              )}

              {!isMembersLoading && activeMembers.length === 0 && (
                <div className="px-2 py-1.5 text-xs text-muted-foreground">
                  <T context="no active org members for assignee">
                    No active users found.
                  </T>
                </div>
              )}

              {activeMembers.map((member) => (
                <SelectItem key={member.userId} value={member.userId}>
                  {member.name} ({member.email})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {membersError && (
            <p className="text-xs text-destructive">
              <T context="error loading org members for assignee">
                Could not load users.
              </T>
            </p>
          )}

          <div className="space-y-2">
            <Label className="text-sm font-medium">
              <T context="label for task assignee user groups picker">
                User Groups
              </T>
            </Label>
            <MultiSelect
              options={groupOptions}
              value={selectedGroupOptions}
              onChange={(selected) =>
                onConfigChange({
                  ...config,
                  assigneeType: 'user',
                  assigneeUserGroupIds: selected.map((s) => s.id),
                })
              }
              placeholder={gt('Pick one or more user groups', {
                context: 'task assignee groups picker placeholder',
              })}
              disabled={readOnly}
            />
            <p className="text-xs text-muted-foreground">
              <T context="help under the task assignee groups picker">
                Members watch the task; when no Assignee User is set, the first
                member becomes the assignee.
              </T>
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">
              <T context="label for task assignee contacts picker">
                Address Book Contacts
              </T>
            </Label>
            <MultiSelect
              options={contactOptions}
              value={selectedContactOptions}
              onChange={(selected) =>
                onConfigChange({
                  ...config,
                  assigneeType: 'user',
                  assigneeContactIds: selected.map((s) => s.id),
                })
              }
              placeholder={gt('Pick one or more contacts', {
                context: 'task assignee contacts picker placeholder',
              })}
              disabled={readOnly}
            />
            <p className="text-xs text-muted-foreground">
              <T context="help under the task assignee contacts picker">
                Contacts are matched to users by email; contacts without a user
                account are skipped.
              </T>
            </p>
          </div>
        </div>
      )}

      {assigneeMode === 'agent' && (
        <div className="space-y-2">
          <Label
            htmlFor="assigneeAgentDefinitionId"
            className="text-sm font-medium"
          >
            <T context="label for AI agent assignee dropdown">
              Assignee AI Agent
            </T>
          </Label>
          <Select
            value={selectedAgentDefinitionId}
            onValueChange={handleAgentAssigneeChange}
            disabled={
              readOnly ||
              isAgentDefinitionsLoading ||
              agentDefinitions.length === 0
            }
          >
            <SelectTrigger id="assigneeAgentDefinitionId" className="w-full">
              <SelectValue
                placeholder={gt('Select an AI agent...', {
                  context: 'placeholder select AI agent assignee',
                })}
              />
            </SelectTrigger>
            <SelectContent>
              {isAgentDefinitionsLoading && (
                <div className="px-2 py-1.5 text-xs text-muted-foreground">
                  <T context="loading AI agent definitions">
                    Loading AI agents...
                  </T>
                </div>
              )}

              {!isAgentDefinitionsLoading && agentDefinitions.length === 0 && (
                <div className="px-2 py-1.5 text-xs text-muted-foreground">
                  <T context="no AI agents available for assignee">
                    No AI agents found.
                  </T>
                </div>
              )}

              {agentDefinitions.map((definition) => (
                <SelectItem key={definition.id} value={definition.id}>
                  {definition.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {agentDefinitionsError && (
            <p className="text-xs text-destructive">
              <T context="error loading AI agents for assignee">
                Could not load AI agents.
              </T>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
